// Blocco azioni Iren del pannello Invio Pratiche (estratto move-only, Fase 5).
import { Send, XCircle, Lock } from "lucide-react";
import type { IrenPrefill } from "@/types/admin";
import { STATI_CONCLUSI } from "./invioPratiche.shared";
import type { UnifiedRow } from "./invioPratiche.shared";

export interface PannelloAzioniIrenProps {
  prefill: IrenPrefill;
  rows: UnifiedRow[]; selected: string;
  sending: boolean; annulling: boolean; jobRunning: boolean; runningSubmit: boolean;
  setConfirm: (c: { msg: string; danger?: boolean; onYes: () => void } | null) => void;
  onInvia: () => void;
  onAzzera: () => void;
}

export function PannelloAzioniIren(props: PannelloAzioniIrenProps) {
  const { prefill, rows, selected, sending, annulling, jobRunning, runningSubmit, setConfirm, onInvia, onAzzera } = props;
    // Iren: una sola azione, "Invia definitivamente" (one-shot: manda subito
    // l'email di firma reale al cliente), disponibile SOLO se non esiste ancora
    // un carrello Iren (!hasIren). Richiede gli estremi documento identità
    // confermati: bloccato lato servizio da gate_estremi_documento, esposto qui
    // via prefill.submit_bloccato_motivo (Task 6/7).
    //
    // 2026-07-31: rimosso il bottone "Crea bozza". A differenza di A2A, dove il
    // submit è incrementale e la bozza è lavoro che resta, l'invio Iren
    // ricostruisce il carrello da zero: la bozza non era un passo verso l'invio,
    // lasciava una pratica inerte nel back office Iren e — con cIrenPraticaId
    // valorizzato — costringeva a passare da "Azzera stato" per poter inviare.
    // Come prova generale non serviva: ogni fallimento (precheck, venditore,
    // documento, carrello) blocca l'invio definitivo PRIMA della submit, quindi
    // prima che al cliente arrivi qualsiasi cosa. Vedi memoria
    // project-iren-bozza-vicolo-cieco.
    //
    // Nessun annullo remoto esposto da Iren: creato un carrello (iren_pratica_id)
    // o con un job "in_corso" impiccato, l'unica azione è azzerare lo stato locale
    // (force=true rifarebbe un NUOVO carrello — footgun, non esposto qui).
    const busy = sending || annulling || jobRunning;
    const selRow = rows.find((r) => r.opp_id === selected);
    const hasIren = !!(selRow?.iren_pratica_id || prefill.iren_pratica_id);
    // pratiche CONCLUSE su Iren (accettata/attivata/annullata) NON azzerabili da
    // qui: contratti vivi, footgun (stesso guard di "annullabile" per A2A).
    const concluded = STATI_CONCLUSI.has(selRow?.stato ?? "");
    const azzerabile = (hasIren || selRow?.stato === "in_corso") && !concluded;
    const canSubmit = prefill.supportato && prefill.has_documento && !busy
      && !azzerabile && !concluded && !prefill.submit_bloccato_motivo;
    return (
      <div className="space-y-3">
        {!azzerabile && !concluded && (
          <div className="space-y-1">
            <button
              type="button"
              disabled={!canSubmit}
              title={prefill.submit_bloccato_motivo ?? undefined}
              onClick={() => setConfirm({
                danger: false,
                msg: "La pratica verrà INVIATA DEFINITIVAMENTE a Iren e partirà l'email di firma "
                  + "reale al cliente. Confermi?",
                onYes: () => { setConfirm(null); onInvia(); },
              })}
              className="w-full px-4 py-2.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-45 disabled:cursor-not-allowed font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              <span className="flex flex-col items-center leading-tight">
                <span>{jobRunning && runningSubmit ? "Invio in corso…" : (sending ? "Avvio…" : "Invia definitivamente")}</span>
                <span className="text-[11px] font-normal opacity-70">invio reale · email di firma al cliente</span>
              </span>
            </button>
            {!prefill.has_documento && (
              <p className="text-[11px] text-amber-700 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Carica il documento identità su EspoCRM per abilitare l'invio.
              </p>
            )}
          </div>
        )}

        {azzerabile && (
          <div className="pt-2 border-t border-border">
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm({
                danger: true,
                msg: "Azzera lo stato di invio Iren su CRM. Nessun annullo remoto: una pratica già creata su Iren NON viene cancellata. Confermi?",
                onYes: () => { setConfirm(null); onAzzera(); },
              })}
              className="w-full px-4 py-2 text-sm border border-destructive/50 text-destructive rounded-lg hover:bg-destructive/10 transition-colors disabled:opacity-50 font-medium cursor-pointer flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {annulling ? "Azzeramento…" : "Azzera stato"}
            </button>
          </div>
        )}
      </div>
    );
}
